import { cn } from "@core/utils/cn";
import { Icon } from "@core/components";
import { useTranslate } from "@core/hooks";
import type { IconKey } from "@core/assets/icons";
import type { RGBA, ShapeKind } from "@modules/docs/annotations/types/TAnnotation";

export type AnnotationToolbarProps = {
  selectedShape: ShapeKind;
  onShapeChange: (shape: ShapeKind) => void;
  selectedColor: RGBA;
  onColorChange: (color: RGBA) => void;
  canAnnotate: boolean;
  onSave?: () => void;
  isSaving?: boolean;
  hasChanges?: boolean;
};

type ShapeOption = {
  shape: ShapeKind;
  icon: IconKey;
};

const SHAPE_OPTIONS: ShapeOption[] = [
  { shape: "rectangle", icon: "square" },
  { shape: "circle", icon: "circle" },
  { shape: "arrow", icon: "arrow-up-right" },
  { shape: "cloud", icon: "cloud" },
];

type ColorSwatch = {
  label: string;
  color: RGBA;
};

// RGBA is now a template-literal string — compare directly with ===
const COLOR_SWATCHES: ColorSwatch[] = [
  { label: "Red", color: "rgba(239, 68, 68, 1)" },
  { label: "Orange", color: "rgba(249, 115, 22, 1)" },
  { label: "Yellow", color: "rgba(234, 179, 8, 1)" },
  { label: "Green", color: "rgba(34, 197, 94, 1)" },
  { label: "Blue", color: "rgba(59, 130, 246, 1)" },
  { label: "Purple", color: "rgba(168, 85, 247, 1)" },
];

export const AnnotationToolbar = ({
  selectedShape,
  onShapeChange,
  selectedColor,
  onColorChange,
  canAnnotate,
  onSave,
  isSaving,
  hasChanges,
}: AnnotationToolbarProps) => {
  const { t } = useTranslate("documents");

  return (
    <div className="flex flex-wrap items-center gap-4 px-4 py-2 bg-white border border-neutral-200 rounded-xl">
      {/* Section 1 — Shape picker */}
      <div
        className={cn(
          "flex items-center gap-2",
          !canAnnotate && "opacity-40 pointer-events-none",
        )}
      >
        <span className="text-xs text-neutral-400 font-medium">{t("annotations.toolbar.shape")}</span>
        {SHAPE_OPTIONS.map(({ shape, icon }) => (
          <button
            key={shape}
            type="button"
            onClick={() => onShapeChange(shape)}
            className={cn(
              "w-8 h-8 rounded-lg border flex items-center justify-center transition-colors",
              selectedShape === shape
                ? "bg-primary-50 border-primary-300 text-primary-600"
                : "bg-white border-neutral-200 text-neutral-500",
            )}
          >
            <Icon name={icon} className="w-4 h-4" strokeWidth={1.6} />
          </button>
        ))}
      </div>

      {/* Divider */}
      <div className="w-px h-5 bg-neutral-200 shrink-0" />

      {/* Section 2 — Color swatches */}
      <div
        className={cn(
          "flex items-center gap-2",
          !canAnnotate && "opacity-40 pointer-events-none",
        )}
      >
        <span className="text-xs text-neutral-400 font-medium">{t("annotations.toolbar.color")}</span>
        {COLOR_SWATCHES.map(({ label, color }) => (
          <button
            key={label}
            type="button"
            onClick={() => onColorChange(color)}
            className={cn(
              "w-6 h-6 rounded-full border-2 cursor-pointer transition-all",
              selectedColor === color
                ? "border-neutral-800 scale-110"
                : "border-transparent",
            )}
            style={{ backgroundColor: color }}
            aria-label={label}
          />
        ))}
      </div>

      {/* Section 3 — Right side: read-only badge or save button */}
      <div className="ml-auto flex items-center gap-3">
        {!canAnnotate && (
          <span className="text-xs text-neutral-400 font-medium">{t("annotations.toolbar.read-only")}</span>
        )}
        {canAnnotate && onSave && (
          <button
            type="button"
            onClick={onSave}
            disabled={!hasChanges || isSaving}
            className={cn(
              "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors",
              hasChanges && !isSaving
                ? "bg-primary-600 text-white hover:bg-primary-700"
                : "cursor-not-allowed bg-neutral-100 text-neutral-400",
            )}
          >
            {isSaving ? (
              <Icon name="rotate-ccw" className="h-3 w-3 animate-spin" strokeWidth={1.6} />
            ) : (
              <Icon name="save" className="h-3 w-3" strokeWidth={1.6} />
            )}
            {isSaving ? t("annotations.toolbar.saving") : t("annotations.toolbar.save")}
          </button>
        )}
      </div>
    </div>
  );
};
